import { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';

import { Capacitor } from '@capacitor/core';

import { useBackLayerStack } from '../../../context/BackLayerStackContext';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import config from '../../../envConfig';
import { useFeature } from '../../../hooks/use-feature';
import { useFirebaseAuth } from '../../../hooks/useFirebaseAuth';
import { AgreementType } from '../../../redux/api/agreements/interfaces';
import { useGetVersion } from '../../../redux/api/appConfigApi';
import { resetState } from '../../../redux/store';
import { AppFeatureKey } from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import { logAuth } from '../../../utils/auth/auth-debug-logger';
import { useStyles } from './Menu.styles';

export interface IMenuProps {
  setShowMenu: (value: boolean) => void;
  onOpenPolicyModal: (agreementType: AgreementType) => void;
}

export interface IPolicyMenuLink {
  agreementType: AgreementType;
  i18nKey: string;
}

const POLICY_MENU_LINKS: IPolicyMenuLink[] = [
  {
    agreementType: AgreementType.PRIVACY_POLICY,
    i18nKey: 'menu.privacyPolicyLink',
  },
  {
    agreementType: AgreementType.TERMS_OF_USE,
    i18nKey: 'menu.termsOfUseLink',
  },
  {
    agreementType: AgreementType.COOKIE_POLICY,
    i18nKey: 'menu.cookiePolicyLink',
  },
];

export const useMenu = (props: IMenuProps) => {
  const { setShowMenu, onOpenPolicyModal } = props;
  const location = useLocation();
  const { replaceFull, navigate } = useNavigationStack();
  const { tryDismissTop } = useBackLayerStack();
  const { t } = useTranslation();
  const { logOut } = useFirebaseAuth();
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const isNativePlatform = Capacitor.getPlatform() !== 'web';
  const { data: backendVersionInfo } = useGetVersion(undefined, {
    skip: isNativePlatform,
  });
  const {
    [AppFeatureKey.subscriptions]: subscriptionFeatureActive,
    [AppFeatureKey.donations]: donationsFeatureActive,
    [AppFeatureKey.resourses]: resoursesFeatureActive,
  } = useFeature();

  const planOrDonationsMenuVisible =
    subscriptionFeatureActive || donationsFeatureActive;

  const displayedVersion = isNativePlatform
    ? config.version
    : backendVersionInfo?.currentVersion;

  const isActiveTab = (item: { activeKey: string }) =>
    location.pathname.split('/').filter(Boolean)[0] === item.activeKey;

  const onLinkClick = (event: SyntheticEvent, link: string) => {
    event.preventDefault();
    event.stopPropagation();
    const navigateFromMenu = () => {
      replaceFull(link);
      setShowMenu(false);
    };
    if (tryDismissTop(navigateFromMenu)) {
      return;
    }
    navigateFromMenu();
  };

  const handleLogout = () => {
    logAuth('menu:logout', { pathname: location.pathname });
    logOut();
    resetState();
    navigate(Paths.signIn);
  };

  const openPolicyModal = (agreementType: AgreementType) => {
    onOpenPolicyModal(agreementType);
    setShowMenu(false);
  };

  const closeMenu = () => setShowMenu(false);

  return {
    t,
    theme,
    classes,
    planOrDonationsMenuVisible,
    subscriptionFeatureActive,
    donationsFeatureActive,
    resoursesFeatureActive,
    policyMenuLinks: POLICY_MENU_LINKS,
    displayedVersion,
    closeMenu,
    isActiveTab,
    onLinkClick,
    handleLogout,
    openPolicyModal,
  };
};
